/*
 * /auth/attachment-types (page)
 */
import {
	Badge,
	Button,
	Container,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Form,
	InputFormField,
	PageHeader,
	PageWithEffect,
	TextareaFormField,
	toast,
} from "@emr/react-ui"
import type { AttachmentType } from "@emr/db/client"
import { createFileRoute } from "@tanstack/react-router"
import { Loader2, Pencil, Plus, ToggleLeft, ToggleRight } from "lucide-react"
import { useState } from "react"
import { z } from "zod"
import { trpc } from "../../trpc"
import { adminPageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/auth/attachment-types")({
	component: AttachmentTypesPage,
	loader: adminPageLoader,
})

const typeSchema = z.object({
	name: z.string().min(1, "Name is required"),
	description: z.string().optional(),
})
type TypeValues = z.infer<typeof typeSchema>

/* ─── Page ───────────────────────────────────────────────────────────────── */

function AttachmentTypesPage() {
	const [editType, setEditType] = useState<AttachmentType | null>(null)
	const [createOpen, setCreateOpen] = useState(false)

	const { data, isLoading, refetch } = trpc.attachmentType.getAllByFilter.useQuery({})

	const types = data ?? []

	const create = trpc.attachmentType.create.useMutation({
		onSuccess: () => { toast.success("Type created"); refetch(); setCreateOpen(false) },
		onError: (e) => toast.error(e.message),
	})
	const update = trpc.attachmentType.update.useMutation({
		onSuccess: () => { toast.success("Type updated"); refetch(); setEditType(null) },
		onError: (e) => toast.error(e.message),
	})

	function toggleActive(t: AttachmentType) {
		update.mutate({ id: t.id, active: !t.active })
	}

	return (
		<PageWithEffect effect="aurora" centerVertical={false}>
			<Container width="xl" padding="lg" gap="lg">
				<PageHeader
					title="Attachment Types"
					right={{
						Component: (
							<Button size="sm" onClick={() => setCreateOpen(true)}>
								<Plus className="mr-1.5 size-4" />
								New Type
							</Button>
						),
					}}
				/>

				{isLoading ? (
					<div className="flex items-center justify-center py-16">
						<Loader2 className="size-6 animate-spin text-muted-foreground" />
					</div>
				) : types.length === 0 ? (
					<p className="py-12 text-center text-sm text-muted-foreground">
						No attachment types yet. Create one to start categorising uploads.
					</p>
				) : (
					<div className="w-full overflow-x-auto rounded-lg border">
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b bg-muted/40 text-left">
									<th className="px-4 py-3 font-medium">Name</th>
									<th className="px-4 py-3 font-medium">Description</th>
									<th className="px-4 py-3 font-medium">Status</th>
									<th className="px-4 py-3 font-medium" />
								</tr>
							</thead>
							<tbody>
								{types.map((t) => (
									<tr key={t.id} className="border-b last:border-0 hover:bg-muted/20">
										<td className="px-4 py-3 font-medium">{t.name}</td>
										<td className="px-4 py-3 text-muted-foreground">
											{t.description ?? "—"}
										</td>
										<td className="px-4 py-3">
											<Badge variant={t.active ? "secondary" : "outline"}>
												{t.active ? "Active" : "Inactive"}
											</Badge>
										</td>
										<td className="px-4 py-3 text-right">
											<div className="flex items-center justify-end gap-1">
												<Button
													variant="ghost"
													size="icon"
													className="size-8"
													title={t.active ? "Deactivate" : "Activate"}
													onClick={() => toggleActive(t)}
													disabled={update.isPending}
												>
													{t.active
														? <ToggleRight className="size-4 text-green-600" />
														: <ToggleLeft className="size-4 text-muted-foreground" />}
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="size-8"
													title="Edit"
													onClick={() => setEditType(t)}
												>
													<Pencil className="size-4" />
												</Button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</Container>

			{/* Create dialog */}
			<Dialog open={createOpen} onOpenChange={setCreateOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>New Attachment Type</DialogTitle>
					</DialogHeader>
					<Form<TypeValues>
						schema={typeSchema}
						onSubmit={async (v) => {
							try {
								await create.mutateAsync({ name: v.name, description: v.description ?? null })
							} catch (e: any) {
								return e?.message ?? "An error occurred"
							}
						}}
						resetOnSuccess
						className="flex flex-col gap-4"
					>
						<InputFormField name="name" label="Name" placeholder="e.g. Medical Report" required />
						<TextareaFormField name="description" label="Description" placeholder="Optional description" />
						<DialogFooter>
							<Button type="submit" disabled={create.isPending}>
								{create.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
								Create
							</Button>
						</DialogFooter>
					</Form>
				</DialogContent>
			</Dialog>

			{/* Edit dialog */}
			{editType && (
				<Dialog open={!!editType} onOpenChange={(o) => !o && setEditType(null)}>
					<DialogContent className="sm:max-w-md">
						<DialogHeader>
							<DialogTitle>Edit Attachment Type</DialogTitle>
						</DialogHeader>
						<Form<TypeValues>
							schema={typeSchema}
							defaultValues={{ name: editType.name, description: editType.description ?? "" }}
							onSubmit={async (v) => {
								try {
									await update.mutateAsync({ id: editType.id, name: v.name, description: v.description ?? null })
								} catch (e: any) {
									return e?.message ?? "An error occurred"
								}
							}}
							className="flex flex-col gap-4"
						>
							<InputFormField name="name" label="Name" required />
							<TextareaFormField name="description" label="Description" />
							<DialogFooter>
								<Button type="submit" disabled={update.isPending}>
									{update.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
									Save
								</Button>
							</DialogFooter>
						</Form>
					</DialogContent>
				</Dialog>
			)}
		</PageWithEffect>
	)
}
